import { mappedColors } from '@pixee/design-system'
import { z } from 'zod'
import { flareFill, nebulaFill, prismFill, pulseFill } from './resolution-categorization-palette.css.ts'

/**
 * The colours a bucket can take. Buckets store the key rather than the CSS value, so a later change
 * to a swatch's fill reaches buckets that were saved before it.
 */
export const BucketSwatchKeySchema = z.enum([
  'pulse',
  'flare',
  'nebula',
  'ember',
  'haze',
  'prism',
  'neutralLight',
  'neutralDark',
])
export type BucketSwatchKey = z.infer<typeof BucketSwatchKeySchema>

export type BucketSwatch = {
  label: string
  /** Fills the bucket's dot, its bar segment, its funnel rows and its state chips. */
  fill: string
  /** The text on a chip filled with this swatch, chosen to hold 4.5:1 against `fill` in both themes. */
  text: string
}

/**
 * Four fills are vars from `resolution-categorization-palette.css.ts`, which resolve only beneath
 * its `bucketPaletteScope` class.
 */
export const BUCKET_COLOR_SWATCHES: Record<BucketSwatchKey, BucketSwatch> = {
  pulse: { label: 'Pulse', fill: pulseFill, text: mappedColors.neutral.orbit },
  flare: { label: 'Flare', fill: flareFill, text: mappedColors.neutral.orbit },
  nebula: { label: 'Nebula', fill: nebulaFill, text: mappedColors.neutral.orbit },
  ember: { label: 'Ember', fill: mappedColors.secondary.spark, text: mappedColors.neutral.orbit },
  haze: { label: 'Haze', fill: mappedColors.secondary.haze, text: mappedColors.neutral.orbit },
  prism: { label: 'Prism', fill: prismFill, text: mappedColors.neutral.orbit },
  neutralLight: { label: 'Neutral light', fill: mappedColors.neutral.neutral30, text: mappedColors.neutral.orbit },
  // `orbit` text on neutral60 is 2.1:1 in both themes; neutral10 flips with the fill and holds 7.7:1.
  neutralDark: { label: 'Neutral dark', fill: mappedColors.neutral.neutral60, text: mappedColors.neutral.neutral10 },
}

/** The order the bucket editor's picker lays the swatches out in. */
export const BUCKET_COLOR_SWATCH_KEYS = BucketSwatchKeySchema.options
